import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { resetError } from '_store/user/slice';

import Button from 'components/atomic/Button';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import Text from 'components/atomic/layouts/Text';

const StyledRow = styled(Row)`
  padding-top: ${theme.spaces.regular}px;
`;

const StyledLinkContainer = styled.div`
  a {
    font-family: 'Inter';
    font-size: ${theme.fontSizes.small}px;
    font-weight: 400;
    line-height: 12px;
    color: ${theme.colors.gray.secondary};

    &:hover {
      color: ${theme.colors.indigo.primary};
    }
  }
`;

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const SetPasswordForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const dispatch = useDispatch();

  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const values = useWatch({ control });

  const handleRedirectClick = () => {
    dispatch(resetError());
  };

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12}>
          <Text
            size={theme.fontSizes.smallX}
            weight={400}
            color={theme.colors.gray.secondary}
            bottomSpace={theme.spaces.medium}
          >
            Create your dashboard password below. Passwords must contain 8 or more characters, including an upper case
            letter, number, and symbol.
          </Text>
        </Col>
        <Col xs={12}>
          <BaseField {...getFieldProps(FIELD_KEYS.newPassword)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.confirmation, {
              validationOptions: { values },
            })}
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
      </Row>
      <StyledRow>
        <Col xs={8} />
        <Col xs={4}>
          <Button type="submit" disabled={submitDisabled} color={theme.colors.indigo.primary}>
            Set Password
          </Button>
        </Col>
      </StyledRow>

      {loading && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Processing...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}

      {error?.code === 403 && (
        <StyledRow>
          <Col xs={6}>
            <StyledLinkContainer onClick={handleRedirectClick}>
              <Link to="../login">Return to Log In</Link>
            </StyledLinkContainer>
          </Col>
          <Col xs={6} className="d-flex justify-content-end">
            <StyledLinkContainer onClick={handleRedirectClick}>
              <Link to="../forgot-password">Request Reset Code</Link>
            </StyledLinkContainer>
          </Col>
        </StyledRow>
      )}
    </Form>
  );
};

SetPasswordForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default SetPasswordForm;
